import { reactive, watch } from 'vue'
import {
  api,
  ApiError,
  invalidatePreviousSessionResponses,
  getApplicationCatalog,
  getPlatformContext,
  getPts2Context,
  selectNaftaPosSource as selectNaftaPosSourceRequest,
  selectPhysicalStation as selectPhysicalStationRequest,
  selectPts2Controller as selectPts2ControllerRequest,
} from './api.js'
import { can } from './access/permissions.js'

const guestLanguageStorageKey = 'next-reports-guest-language'
const supportedLanguages = new Set(['uk', 'ru', 'en'])

function readGuestLanguage() {
  try {
    const savedLanguage = window.localStorage.getItem(guestLanguageStorageKey)
    return supportedLanguages.has(savedLanguage) ? savedLanguage : 'uk'
  } catch {
    return 'uk'
  }
}

export const session = reactive({
  initialized: false,
  user: null,
  guestLanguage: readGuestLanguage(),
  applications: [],
  platformEntryPath: null,
  platformContext: null,
  pts2Context: null,
  platformShellLoaded: false,
})

let contextIntent = 0
let catalogRequest = 0
let shellRequest = 0
let pts2Request = 0
const principalKey = () => JSON.stringify([session.user?.id ?? session.user?.username, session.user?.accessVersion])

function invalidateContextRequests() {
  contextIntent++
  catalogRequest++
  shellRequest++
  pts2Request++
}

watch(principalKey,
  () => { invalidatePreviousSessionResponses(); resetPlatformShell() }, { flush: 'sync' })

function contextScope(stationSpecific = false) {
  const principal = principalKey(), intent = contextIntent
  const stationId = session.platformContext?.activeStationId
  return () => Boolean(session.user) && principalKey() === principal && contextIntent === intent
    && (!stationSpecific || session.platformContext?.activeStationId === stationId)
}

export function beginContextSelection() {
  invalidateContextRequests()
  return contextScope()
}

async function currentResponse(request, isCurrent) {
  try {
    const result = await request
    return isCurrent() ? result : null
  } catch (error) {
    if (isCurrent()) throw error
    return null
  }
}

function resetPlatformShell() {
  invalidateContextRequests()
  session.applications = []
  session.platformEntryPath = null
  session.platformContext = null
  session.pts2Context = null
  session.platformShellLoaded = false
}

export async function refreshApplicationCatalog() {
  if (!session.user) return null
  const request = ++catalogRequest, scope = contextScope(true)
  const catalog = await currentResponse(getApplicationCatalog(), () => request === catalogRequest && scope())
  if (!catalog) return null
  session.applications = catalog.applications ?? []
  session.platformEntryPath = catalog.platformEntryPath ?? null
  return catalog
}

export async function loadPlatformShell({ force = false } = {}) {
  if (!session.user) {
    resetPlatformShell()
    return null
  }
  if (session.platformShellLoaded && !force) {
    return {
      applications: session.applications,
      platformEntryPath: session.platformEntryPath,
      context: session.platformContext,
    }
  }
  const request = ++shellRequest, scope = contextScope(true)
  const catalogGeneration = ++catalogRequest
  const result = await currentResponse(Promise.all([getPlatformContext(), getApplicationCatalog()]),
    () => request === shellRequest && catalogGeneration === catalogRequest && scope())
  if (!result) return null
  const [context, catalog] = result
  session.platformContext = context
  session.applications = catalog.applications ?? []
  session.platformEntryPath = catalog.platformEntryPath ?? null
  session.platformShellLoaded = true
  return { ...catalog, context }
}

export async function choosePhysicalStation(stationId) {
  const isCurrent = beginContextSelection()
  const context = await currentResponse(selectPhysicalStationRequest(stationId), isCurrent)
  if (!context) return null
  if (Number(context.activeStationId) !== Number(stationId)) {
    throw new ApiError('Station selection response is invalid', 502, 'invalid_context_response')
  }
  session.platformContext = context
  session.pts2Context = null
  await refreshApplicationCatalog()
  if (!isCurrent()) return null
  session.platformShellLoaded = true
  return session.platformContext
}

export async function chooseNaftaPosSource(sourceInstanceId) {
  const isCurrent = beginContextSelection()
  const stationId = session.platformContext?.activeStationId
  const context = await currentResponse(selectNaftaPosSourceRequest(sourceInstanceId), isCurrent)
  if (!context) return null
  if (context.activeStationId !== stationId || Number(context.activeNaftaPosInstanceId) !== Number(sourceInstanceId)) {
    throw new ApiError('Source selection response is invalid', 502, 'invalid_context_response')
  }
  session.platformContext = context
  session.platformShellLoaded = true
  return session.platformContext
}

export async function loadPts2Context({ force = false } = {}) {
  if (session.pts2Context && !force) return session.pts2Context
  const request = ++pts2Request, scope = contextScope(true)
  const context = await currentResponse(getPts2Context(), () => request === pts2Request && scope())
  if (!context) return null
  session.pts2Context = context
  return session.pts2Context
}

// Revalidate both grants and station timezone before displaying a retained journal page.
export async function loadPts2HistoryContext({ isCurrent = () => true } = {}) {
  const request = ++pts2Request, scope = contextScope(true)
  const stationId = session.platformContext?.activeStationId
  const result = await currentResponse(Promise.all([getPlatformContext(), getPts2Context()]),
    () => request === pts2Request && scope() && isCurrent())
  if (!result) return null
  const [platform, pts2] = result
  if (platform.activeStationId !== stationId || pts2.stationId !== platform.activeStationId
    || (pts2.activeControllerId != null && !pts2.controllers?.some((item) =>
      Number(item.id) === Number(pts2.activeControllerId) && Number(item.stationId) === Number(pts2.stationId)))) {
    throw new ApiError('History context response is invalid', 502, 'invalid_context_response')
  }
  session.platformContext = platform
  session.pts2Context = pts2
  return pts2
}

export async function choosePts2Controller(controllerId) {
  const isCurrent = beginContextSelection()
  const stationId = session.platformContext?.activeStationId
  const context = await currentResponse(selectPts2ControllerRequest(controllerId), isCurrent)
  if (!context) return null
  if (context.stationId !== stationId || Number(context.activeControllerId) !== Number(controllerId)) {
    throw new ApiError('Controller selection response is invalid', 502, 'invalid_context_response')
  }
  session.pts2Context = context
  return session.pts2Context
}

export function roleCode(user = session.user) {
  return typeof user?.role === 'object' ? user.role.systemCode : user?.role
}

export function hasPermission(permission, user = session.user) {
  return can(user, permission)
}

export function changeGuestLanguage(language) {
  if (!supportedLanguages.has(language)) return
  session.guestLanguage = language
  try {
    window.localStorage.setItem(guestLanguageStorageKey, language)
  } catch {
    // The login screen remains usable when browser storage is unavailable.
  }
}

export async function restoreSession() {
  try {
    const result = await api('/auth/me')
    session.user = result.user
  } catch (error) {
    if (error.status !== 401) throw error
    session.user = null
    resetPlatformShell()
  } finally {
    session.initialized = true
  }
}

export async function signIn(username, password) {
  const result = await api('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  })
  session.user = result.user
  session.initialized = true
  resetPlatformShell()
  return result.user
}

export async function signOut() {
  await api('/auth/logout', { method: 'POST' })
  session.user = null
  session.initialized = true
  resetPlatformShell()
}

export async function changeOwnPassword(currentPassword, newPassword) {
  const result = await api('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  })
  session.user = result.user
  return result.user
}

export async function changeReportLanguage(language) {
  const result = await api('/preferences/report-language', {
    method: 'PUT',
    body: JSON.stringify({ language }),
  })
  session.user = result.user
  return result.user
}
